import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { CatalogRow } from '@/shared/api/generated/catalog';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import type { BadgeTone } from '@/shared/ui/StatusBadge';

/* One cell, and the most consequential one on the screen.
 *
 * A data-quality catalog that shows only `active / 1` for every row tells a steward nothing. What they
 * are scanning for is which datasets hold personal data and which have not been looked at — so this
 * cell has to distinguish **four** situations that are easy to collapse into two:
 *
 *   - nobody has classified it            -> muted, and labelled "Not classified"
 *   - classified, nothing flagged         -> ok
 *   - classified, N columns flagged       -> ok tone, N shown
 *   - the classifier could not decide     -> its own hue, used nowhere else
 *
 * The first and second are the pair that matter. Both have `pii_columns: 0`, and rendering the count
 * alone would show them identically — which is how a table full of unexamined datasets reads as a
 * table full of clean ones. `classified` is checked first for exactly that reason.
 *
 * `stale` is a warning rather than a verdict: the classification was valid against a schema that has
 * since moved, so what it says may no longer be true of the columns that are there now.
 */

const TONE: Readonly<Record<string, BadgeTone>> = {
  approved: 'ok',
  /* A classifier's verdict, not a human's. `info` rather than `ok` because it is not authoritative
   * until somebody accepts it, and the review queue is where that happens. */
  proposed: 'info',
  stale: 'warn',
  pending: 'info',
  /* Violet, and it appears nowhere else in the product. "We could not tell" is not a point on a
   * red-amber-green scale — see tokens.css. */
  inconclusive: 'unknown',
};

export function PiiCell({ row }: { readonly row: CatalogRow }): ReactElement {
  const { t } = useTranslation();

  /* Read before the count, always. A dataset nobody has examined has `pii_columns: 0`, and treating
   * that as "no personal data" is the one mistake this cell exists to prevent. */
  if (!row.classified) {
    return (
      <StatusBadge
        tone="neutral"
        label={t('catalog.pii.unclassified.label')}
        title={t('catalog.pii.unclassified.meaning')}
      />
    );
  }

  const state = row.classification ?? 'pending';
  const tone = TONE[state] ?? 'neutral';

  /* An inconclusive classification has no meaningful count — it did not finish deciding — so it
   * reports its state and nothing else. Showing "0 columns" beside it would be the same false
   * reassurance as showing it for an unclassified dataset. */
  if (state === 'inconclusive' || state === 'pending') {
    return (
      <StatusBadge
        tone={tone}
        label={t(`review.state.${state}.label`)}
        title={t(`review.state.${state}.meaning`)}
      />
    );
  }

  if (row.pii_columns === 0) {
    return (
      <StatusBadge
        tone={tone}
        label={t('catalog.pii.clean.label')}
        title={t('catalog.pii.clean.meaning')}
      />
    );
  }

  /* `approved` is the only state whose count stands alone, because it is the only authoritative one.
   *
   * Everything else names its qualifier in the *text*. Rendering `proposed`, `stale` and `approved`
   * all as "1 column" and distinguishing them by tone alone is conveying information by colour — it
   * fails WCAG 1.4.1, and it fails a colour-blind steward silently. Found by reading the rendered
   * table rather than the code: three rows said "1 column" and meant three different things.
   *
   * The `title` is not a substitute. It is not visible, does not appear on touch, and is not
   * announced by every screen reader. */
  if (state === 'approved') {
    return (
      <StatusBadge
        tone={tone}
        label={t('catalog.pii.count', { count: row.pii_columns })}
        title={t('catalog.pii.countMeaning')}
      />
    );
  }

  return (
    <StatusBadge
      tone={tone}
      label={t('catalog.pii.qualified', {
        count: row.pii_columns,
        state: t(`review.state.${state}.label`),
      })}
      title={t(`review.state.${state}.meaning`)}
    />
  );
}
